import { Button } from '@sortlist-frontend/design-system';
import { FileCode } from '@sortlist-frontend/design-system/icons';
import { useState } from 'react';

import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import type { GuideLabels } from '../types';
import { ToggleItem } from './ToggleItem';

interface RobotsBuilderProps {
  agents: readonly { ua: string; label: string; note: string; checked: boolean }[];
  outTitle: string;
  copyLabel: string;
  labels: Pick<GuideLabels, 'copied' | 'copyFallback'>;
}

export const RobotsBuilder = (props: RobotsBuilderProps) => {
  const { agents, outTitle, copyLabel, labels } = props;
  const [allowed, setAllowed] = useState<readonly boolean[]>(agents.map((agent) => agent.checked));
  const { label, copy } = useCopyToClipboard({ idle: copyLabel, copied: labels.copied, fallback: labels.copyFallback });
  const output = agents
    .map((agent, index) => `User-agent: ${agent.ua}\n${allowed[index] ? 'Allow: /' : 'Disallow: /'}`)
    .join('\n\n');

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[1fr_1fr]">
      <div className="space-y-3">
        {agents.map((agent, index) => (
          <ToggleItem
            key={agent.ua}
            checked={allowed[index]}
            onChange={(value) => setAllowed(allowed.map((current, i) => (i === index ? value : current)))}>
            <strong className="font-semibold text-secondary-950">{agent.label}</strong>
            {agent.note !== '' ? <span className="text-secondary-500"> · {agent.note}</span> : null}
          </ToggleItem>
        ))}
      </div>
      <div className="rounded-2xl border border-primary-200 bg-primary-50 p-4 md:p-5 lg:sticky lg:top-24">
        <p className="mb-2 flex items-center gap-2 font-bold text-secondary-950">
          <FileCode size={18} aria-hidden="true" />
          {outTitle}
        </p>
        <pre className="overflow-x-auto rounded-xl bg-secondary-950 p-4 text-xs leading-relaxed text-white">
          {output}
        </pre>
        <div className="mt-4">
          <Button color="primary" variant="bordered" size="md" onPress={() => void copy(output)}>
            {label}
          </Button>
        </div>
      </div>
    </div>
  );
};
